import { LoadingButton } from '@mui/lab'
import { Autocomplete, Box, Card, CardActions, CardContent, CardHeader, Divider, Grid, MenuItem, Select, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetMerchantSmsProviderQuery, useUpdateMerchantSmsProviderMutation } from 'src/_api/smsProviderApi'
import { SMS_PROVIDER_SCHEMA } from '../ValidationSchemas'
import runJoiValidate from "src/_utils/runJoiValidate"
import { cloneDeep, omit, set } from 'lodash'

// Available SMS providers
const smsProviders = [
    { value: 'default', label: 'Default' },
    { value: 'unifonic', label: 'Unifonic' },
    { value: 'taqnyat', label: 'Taqnyat' },
]

export default function SmsProviderCard({ merchantId }) {
    const { t } = useTranslation()

    const { data: smsProvider, isLoading: isLoadingProvider } = useGetMerchantSmsProviderQuery(merchantId)
    const [updateSmsProvider, { isLoading, isSuccess, error, reset }] = useUpdateMerchantSmsProviderMutation()

    const [formData, setFormData] = useState({
        provider: '',
        domain: '',
        settings: {
            apiKey: '',
            senderId: ''
        }
    })

    const [validationErrors, setValidationErrors] = useState(null)

    // Load existing data when component mounts or data changes
    useEffect(() => {
        if (smsProvider) {
            setFormData({
                provider: smsProvider?.provider || '',
                domain: smsProvider?.domain || '',
                settings: {
                    apiKey: smsProvider?.settings?.apiKey || '',
                    senderId: smsProvider?.settings?.senderId || '',
                }
            })
        }
    }, [smsProvider])

    // Reset success/error states when form changes
    useEffect(() => {
        if (isSuccess || error) {
            const timer = setTimeout(() => {
                reset()
            }, 3000)
            return () => clearTimeout(timer)
        }
    }, [isSuccess, error, reset])

    const handleChange = (event) => {
        const { name, value } = event.target

        setFormData(prev => {
            let clone = cloneDeep(prev);
            set(clone, name, value);
            return clone;
        })

        // Clear validation error for this field
        setValidationErrors(prev => omit(prev, [name]));
    }

    const handleSubmit = async () => {
        try {
            setValidationErrors(null)
            reset()

            let payload = cloneDeep(formData)
            if (payload.domain && !payload.domain.endsWith('/')) {
                payload.domain = payload.domain + '/';
            }
            const errors = runJoiValidate(SMS_PROVIDER_SCHEMA, payload)

            if (!!errors) {
                setValidationErrors(errors)
                return
            }

            await updateSmsProvider({
                merchantId,
                payload
            }).unwrap()

        } catch (err) {
            // Error is handled by the mutation hook
            console.log('Failed to update SMS provider:', err)
        }
    }


    return (
        <Card>
            <CardHeader title={t("SMS Provider Settings")} />
            <Divider />
            <CardContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            name="provider"
                            value={smsProviders.find(el => el.value === formData.provider) ?? null}
                            options={smsProviders}
                            onChange={(event, newValue) => {
                                handleChange({ target: { name: "provider", value: newValue?.value } })
                            }}
                            size="small"
                            fullWidth
                            error={!!validationErrors?.provider}
                            helperText={validationErrors?.provider ?? ''}
                            renderInput={(params) => <TextField label={t("SMS Provider")} {...params} />}
                        />

                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            placeholder={t("Ex: https://www.example.com/")}
                            name="domain"
                            size="small"
                            value={formData?.domain ?? ''}
                            onChange={handleChange}
                            error={!!validationErrors?.domain ?? ''}
                            helperText={validationErrors?.domain ?? ''}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={`${t("API Key")} *`}
                            name="settings.apiKey"
                            size="small"
                            value={formData?.settings?.apiKey}
                            onChange={handleChange}
                            error={!!validationErrors?.settings?.apiKey ?? ''}
                            helperText={validationErrors?.settings?.apiKey ?? ''}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={`${t("Sender ID")} *`}
                            name="settings.senderId"
                            size="small"
                            value={formData?.settings?.senderId ?? ''}
                            onChange={handleChange}
                            error={!!validationErrors?.settings?.senderId}
                            helperText={validationErrors?.settings?.senderId ?? ''}
                        />
                    </Grid>

                </Grid>
            </CardContent>
            <Divider />
            <CardActions>
                {isSuccess && <Typography color="success.main">{t("Done successfully")}</Typography>}
                {!!error && <Typography color="error">{error}</Typography>}
                <Box mr="auto" />
                <LoadingButton
                    variant="contained"
                    sx={{ textTransform: "none" }}
                    loading={isLoading || isLoadingProvider}
                    onClick={handleSubmit}
                    disabled={!formData.provider}
                >
                    {t("Confirm")}
                </LoadingButton>
            </CardActions>
        </Card>
    )
}
